import 'react-cmdk/dist/cmdk.css';
import * as Sentry from '@sentry/react';
import CommandPalette, { filterItems, getItemIndex } from 'react-cmdk';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';

import { usePrivilegeContext } from '@feedflo/access-control';
import { censorTypes, useCensor } from '../../utils/hooks/useCensor';
import { algorithmVersionAtom, featureFlagsAtom } from '../../utils/jotaiAtoms';
import useUser from '../../utils/hooks/useUser';
import './CommandPalette.scss';

const UPDATE_USER_GQL = gql`
  mutation CommandPalette_updateUserUnitPreferences($userID: uuid, $isMetric: Boolean) {
    update_user(where: { id: { _eq: $userID } }, _set: { is_metric: $isMetric }) {
      affected_rows
    }
  }
`;

const GET_BARNS_QUERY = gql`
  query CommandPalette_GetBarns {
    barn: farm(
      where: { deleted_at: { _is_null: true }, organization: { deleted_at: { _is_null: true } } }
      order_by: [{ organization: { name: asc_nulls_last } }, { name: asc_nulls_last }, { id: asc }]
    ) {
      name
      value: id
      organization {
        id
        name
      }
    }
  }
`;

const VALIDATE_ALGORITHM_VERSION_GQL = gql`
  query CommandPalette_ValidateAlgorithmVersion($version: String!) {
    algorithm(where: { version: { _eq: $version } }, limit: 1) {
      version
    }
  }
`;

/**
 * A Command Palette, for STAFF ONLY to invoke custom algs, feature flags, etc.
 * @returns React Component
 */
const FeedFloCommandPalette = () => {
  const { censor } = useCensor();
  const { user, loading } = useUser();
  const navigate = useNavigate();
  const [page] = useState('root');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [, setAlgorithmVersion] = useAtom(algorithmVersionAtom);
  const [features, setFeatureFlagArray] = useAtom(featureFlagsAtom);
  const [updateUserUnitPreferences] = useMutation(UPDATE_USER_GQL, {
    refetchQueries: ['AppGlobalInfoQuery'],
  });
  const { data: barnsData } = useQuery(GET_BARNS_QUERY);
  const [validateAlgorithmVersion] = useLazyQuery(VALIDATE_ALGORITHM_VERSION_GQL);
  const { privileges, loading: privilegesLoading, error: privilegesError } = usePrivilegeContext();

  useEffect(() => {
    function handleKeyDown(e) {
      // Note: navigator.platform is deprecated. Some suggest User Agent sniffing as an alternative
      //       however this is the exact check that react-cmdk itself uses for their hook
      if ((navigator?.platform?.toLowerCase().includes('mac') ? e.metaKey : e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();

        setOpen((currentValue) => {
          return !currentValue;
        });
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [user, loading]);

  const STAFF_ONLY_MENU_ITEMS = [
    {
      heading: 'Data Science Stuff',
      id: 'datasci',
      icon: 'BeakerIcon',
      items: [
        {
          id: 'setalg',
          icon: 'BeakerIcon',
          showType: false,
          children: 'Set Algorithm Version',
          onClick: async () => {
            const alg = prompt('Enter Algorithm Version to use');
            if (alg) {
              try {
                const { data } = await validateAlgorithmVersion({ variables: { version: alg } });
                if (data?.algorithm?.length > 0) {
                  setAlgorithmVersion(alg);
                  toast.success(`Algorithm version set to: ${alg}`, {
                    position: 'top-right',
                    autoClose: 3000,
                    closeOnClick: true,
                  });
                } else {
                  toast.error(`Invalid algorithm version: "${alg}" not found in database`, {
                    position: 'top-right',
                    autoClose: 5000,
                    closeOnClick: true,
                  });
                  Sentry?.captureMessage(`Invalid algorithm version attempted: ${alg}`);
                }
              } catch (error) {
                console.error('Error validating algorithm version:', error);
                toast.error('Failed to validate algorithm version', {
                  position: 'top-right',
                  autoClose: 5000,
                  closeOnClick: true,
                });
                Sentry?.captureException(error);
              }
            }
          },
        },
        {
          id: 'resetAlg',
          icon: 'ArrowPathIcon',
          showType: false,
          children: 'Reset Algorithm',
          onClick: () => {
            setAlgorithmVersion(null);
            toast.info('Algorithm version reset to default', {
              position: 'top-right',
              autoClose: 3000,
              closeOnClick: true,
            });
          },
        },
      ],
    },
    {
      heading: 'Misc',
      id: 'dev',
      icon: 'CpuChipIcon',
      items: [
        {
          id: 'gotosettings',
          icon: 'CogIcon',
          showType: false,
          children: 'Go To Settings',
          onClick: () => {
            navigate('/settings');
          },
        },
        {
          id: 'printprivileges',
          icon: 'PrinterIcon',
          children: 'Log Privileges',
          showType: false,
          onClick: () => {
            if (privileges && !privilegesLoading) {
              console.group('Privileges');
              console.log('user: ', user?.name);
              console.log('user ID:', user?.id);

              console.log('Roles', user?.barns);

              console.log('🧑‍💻 User Privileges:', {
                barnPrivileges: privileges.barnPrivileges,
                orgPrivileges: privileges.orgPrivileges,
                featurePrivileges: privileges.featurePrivileges,
              });

              // Log individual privilege counts
              console.log('📊 Privilege Summary:', {
                totalBarnPrivileges: privileges.barnPrivileges.length,
                totalOrgPrivileges: privileges.orgPrivileges.length,
                totalFeaturePrivileges: privileges.featurePrivileges.length,
              });

              // Log all unique privileges
              const allPrivileges = [
                ...privileges.barnPrivileges.flatMap((bp) => bp.privileges),
                ...privileges.orgPrivileges.flatMap((op) => op.privileges),
                ...privileges.featurePrivileges.map((fp) => fp.privilege),
              ];
              console.log('🔓 All Unique Privileges:', [...new Set(allPrivileges)].sort());
              console.groupEnd('Privileges');
            } else if (privilegesError) {
              console.error('Privileges Error:', privilegesError);
            }
          },
        },
        {
          id: 'throwerror',
          icon: 'FireIcon',
          showType: false,
          children: 'Throw Error',
          onClick: () => {
            const msg = prompt('Enter An Error Message');
            throw new Error(msg || 'Test Error - No Text Provided');
          },
        },
        {
          id: 'sentrymessage',
          icon: 'FireIcon',
          showType: false,
          children: 'Sentry Capture Message',
          onClick: () => {
            const msg = prompt('Enter Sentry Message');
            Sentry?.captureMessage(msg || 'Test - Capture Message');
          },
        },
        {
          id: 'sentryexception',
          icon: 'FireIcon',
          showType: false,
          children: 'Sentry Capture Exception',
          onClick: () => {
            try {
              const msg = prompt('Enter Sentry Exception');
              throw new Error(msg);
            } catch (e) {
              Sentry?.captureException(e);
            }
          },
        },
      ],
    },
  ];

  const ALL_USER_MENU_ITEMS = [
    {
      heading: 'Preferences',
      id: 'preferences',
      icon: 'CogIcon',
      items: [
        {
          id: 'toggleMetric',
          icon: 'ArrowsRightLeftIcon',
          showType: false,
          children: `Switch to ${user?.isMetric ? 'Imperial' : 'Metric'} Units`,
          onClick: async () => {
            if (!user?.id) return;
            await updateUserUnitPreferences({
              variables: {
                userID: user.id,
                isMetric: !user.isMetric,
              },
            });
            toast.success(`Switched to ${!user.isMetric ? 'Imperial' : 'Metric'} Units`, {
              position: 'top-right',
              autoClose: 3000,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
          },
        },
      ],
    },
    {
      heading: 'Feature Flags',
      id: 'featureflags',
      icon: 'FlagIcon',
      items: [
        {
          id: 'resetFlags',
          icon: 'ArrowPathIcon',
          showType: false,
          children: 'Disable All Feature Flags',
          onClick: () => {
            setFeatureFlagArray(features.map((old) => ({ ...old, active: false })));
          },
        },
        ...features.map((feature) => {
          // Dynamically generate the list of feature flags, display them and allow us to easily set them
          return {
            id: feature?.name,
            // showType: false,
            icon: feature?.active ? 'CheckCircleIcon' : 'NoSymbolIcon',
            children: (
              <span className="CommandPaletteListItem-text">
                Set {feature?.name} to {feature?.active ? 'inactive' : 'active'}
              </span>
            ),
            keywords: feature?.name.split('_'),
            onClick: () => {
              // Toggle only the feature that we selected
              const newArray = features.map((f) => {
                if (f.name == feature.name) {
                  return { ...f, active: !feature?.active };
                } else {
                  return f;
                }
              });
              // Note: I wanted to use the FeatureFlagsAtomsAtom but
              // ran into render order issues, this array approach seems
              // to work well and doesn't cause issues so far
              setFeatureFlagArray(newArray);
            },
          };
        }),
      ],
    },
  ];

  // Group barns by organization
  const barnsByOrg = (barnsData?.barn || []).reduce((acc, barn) => {
    const orgName = barn.organization?.name || 'Other';

    if (!acc[orgName]) {
      acc[orgName] = [];
    }

    acc[orgName].push(barn);

    return acc;
  }, {});

  // Generate menu items for barns
  const barnMenuSections = Object.entries(barnsByOrg).map(([rawOrgName, barns]) => {
    const orgName = censor(rawOrgName, censorTypes.organization);
    return {
      heading: `Barns – ${orgName}`,
      id: `barns-${orgName}-`,
      icon: 'HomeIcon',
      items: barns.map((barn) => {
        const barnName = censor(barn.name, censorTypes.barn);
        return {
          id: `barn-${barn.value}`,
          icon: 'HomeIcon',
          showType: false,
          children: barnName,
          keywords: [barnName, orgName],
          onClick: () => {
            const currentPath = window.location.pathname;
            const pathParts = currentPath.split('/');
            const hasSubPath = pathParts.length >= 4;
            const subPath = hasSubPath ? pathParts[3] : '';

            if (hasSubPath && (currentPath.includes('/b/') || currentPath.includes('/barns/'))) {
              navigate(`/barns/${barn.value}/${subPath}`);
            } else {
              navigate(`/barns/${barn.value}`);
            }
          },
        };
      }),
    };
  });

  let menuItems = [...ALL_USER_MENU_ITEMS];

  if (user.isStaff) {
    menuItems = menuItems.concat(STAFF_ONLY_MENU_ITEMS);
  }

  menuItems = menuItems.concat(barnMenuSections);

  const filteredItems = filterItems(menuItems, search);

  return (
    <CommandPalette onChangeSearch={setSearch} onChangeOpen={setOpen} search={search} isOpen={open} page={page}>
      <CommandPalette.Page id="root">
        {filteredItems.length ? (
          filteredItems.map((list) => (
            <CommandPalette.List key={list.id} heading={list.heading}>
              {list.items.map(({ id, ...rest }) => (
                <CommandPalette.ListItem key={id} index={getItemIndex(filteredItems, id)} {...rest} />
              ))}
            </CommandPalette.List>
          ))
        ) : (
          <CommandPalette.FreeSearchAction />
        )}
      </CommandPalette.Page>
    </CommandPalette>
  );
};

export default FeedFloCommandPalette;
